import querystring from "query-string";
import axiosInstance, { errorHandler } from "./api";
import { UserSortAdapter } from "./utils";

const USERS_URL_V2 = "/tps/api/v2/fst-iroha-accounts/";
const USERS_URL = "/tps/api/fst-iroha-accounts/";

// FstIrohaAccountResource.getAllMobileIrohaAccounts
const getAllMobileUser = axios => (
  page,
  size,
  filter = "",
  sort = "",
  kyc = "ALL",
) =>
  axios
    .get(
      `${USERS_URL_V2}mobile/?${querystring.stringify({
        page,
        size,
        filter,
        sort: UserSortAdapter(sort),
        kyc,
      })}`,
    )
    .then(({ data, headers }) => ({
      data,
      total: Number(headers["x-total-count"]),
    }))
    .catch(errorHandler);

// FstIrohaAccountResource.getAllDesktopIrohaAccounts
const getAllDesktopUser = axios => (
  page,
  size,
  filter = "",
  sort = "",
  kyc = "ALL",
) =>
  axios
    .get(
      `${USERS_URL_V2}desktop/?${querystring.stringify({
        page,
        size,
        filter,
        sort: UserSortAdapter(sort),
        kyc,
      })}`,
    )
    .then(({ data, headers }) => ({
      data,
      total: Number(headers["x-total-count"]),
    }))
    .catch(errorHandler);

// FstIrohaAccountResource.getAllWebIrohaAccounts
const getAllWebUser = axios => (
  page,
  size,
  filter = "",
  sort = "",
  kyc = "ALL",
) =>
  axios
    .get(
      `${USERS_URL_V2}web/?${querystring.stringify({
        page,
        size,
        filter,
        sort: UserSortAdapter(sort),
        kyc,
      })}`,
    )
    .then(({ data, headers }) => ({
      data,
      total: Number(headers["x-total-count"]),
    }))
    .catch(errorHandler);

// FstIrohaAccountResource.checkFstIrohaAccount
const getUserInfoByAccountId = axios => (accountId = "") =>
  axios
    .get(`${USERS_URL}check-account/${accountId}`)
    .then(({ data }) => data)
    .catch(errorHandler);

// FstIrohaAccountResource.findIrohaAccountByAccountName
const getUserInfoByAccountName = axios => (accountName = "") =>
  axios
    .get(`${USERS_URL_V2}find-by-account-name/${accountName}`)
    .then(({ data }) => data)
    .catch(errorHandler);

// FstIrohaAccountResource.findIrohaAccountByPhone
const getUserInfoByPhoneNumber = axios => (phone = "") =>
  axios
    .get(`${USERS_URL_V2}find-by-primary-contact/${phone}`)
    .then(({ data }) => data)
    .catch(errorHandler);

/**
 * Fetches a list of current user's authorizers
 */
const getUserAuthorizersList = axios => ({
  accountName,
  page,
  size,
  filter,
  sort,
}) =>
  axios
    .get("/authservice/api/v2/user/desktop/authorizers", {
      params: { accountName, page, size, filter, sort },
    })
    .then(({ data }) => ({
      data: data.data.content,
      total: data.data.totalElements,
    }))
    .catch(errorHandler);

/**
 * Fetches a list of all the desktop users excepting the current user itself and his authorizers
 */
const getUserPotentialAuthorizers = axios => ({
  accountName,
  page,
  size,
  filter,
  sort,
}) =>
  axios
    .get("/authservice/api/v2/user/desktop/not-authorizers", {
      params: { accountName, page, size, filter, sort },
    })
    .then(({ data }) => ({
      data: data.data.content,
      total: data.data.totalElements,
    }))
    .catch(errorHandler);

/**
 * Fetches history of add/remove authorizers requests
 */
const getUserAuthorizersHistory = axios => ({
  accountName,
  page,
  size,
  filter,
  sort = "createDate,desc",
}) =>
  axios
    .get("/authservice/api/v2/user/desktop/authorizer-requests", {
      params: { accountName, page, size, filter, sort },
    })
    .then(({ data }) => ({
      data: getUserAuthorizersHistoryAdapter(data.data.content),
      total: data.data.totalElements,
    }))
    .catch(errorHandler);

/**
 * API adapter to make items of history list flat
 */
function getUserAuthorizersHistoryAdapter(data) {
  return data.map(record => ({
    createDate: record.createDate,
    accountName: record.authorizer.accountName,
    status: record.status,
    creatorName: record.creator.accountName,
    changeMadeBy: record.changeMadeBy,
    direction: record.direction,
  }));
}

// UserResource.addAuthorizerToUserByAdmin
/**
 * Add account as authorizer to given user
 */
const addAuthorizer = axios => ({ creatorAccountId, authorizerAccountId }) =>
  axios
    .post("/authservice/api/v1/user/add-authorizer", {
      creatorAccountId,
      authorizerAccountId,
    })
    .then(({ data }) => data)
    .catch(errorHandler);

// UserResource.removeAuthorizer
/**
 * Remove account from authorizers list of given user
 */
const removeAuthorizer = axios => ({ creatorAccountId, authorizerAccountId }) =>
  axios
    .put("/authservice/api/v1/user/remove-authorizer", {
      creatorAccountId,
      authorizerAccountId,
    })
    .then(({ data }) => data)
    .catch(errorHandler);

// WalletTransferResource.paymentGatewayWalletTransfer
const transferMoney = axios => payload =>
  axios
    .post("/tps/api/v2/pg-wallet-transfer", payload)
    .then(({ data }) => data.data)
    .catch(errorHandler);

// AccountRecoveryResource.accountRecoveryRequest
const sendKeyRecoveryRequest = axios => payload =>
  axios
    .post("/tps/api/recovery/account-recovery-request", payload)
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.createRequest
const createChangePhoneRequest = axios => ({
  accountId,
  replacementMobilePrimaryContact,
}) =>
  axios
    .post("/authservice/api/v1/replace-user-primary-contact", {
      accountId,
      replacementMobilePrimaryContact,
    })
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.resendSms
const resendSms = axios => requestId =>
  axios
    .put(
      `/authservice/api/v1/replace-user-primary-contact/resend-sms?requestId=${requestId}`,
    )
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.authorize
const authorizeChangePhoneRequest = axios => requestId =>
  axios
    .put(
      `/authservice/api/v1/replace-user-primary-contact/authorize?requestId=${requestId}`,
    )
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.reject
const rejectChangePhoneRequest = axios => ({ requestId, reason }) =>
  axios
    .put(
      "/authservice/api/v1/replace-user-primary-contact/reject",
      {
        text: reason,
      },
      {
        params: {
          requestId,
        },
      },
    )
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.verify
const verifyChangePhoneRequest = axios => ({ requestId, code }) =>
  axios
    .put("/authservice/api/v1/replace-user-primary-contact/verify", undefined, {
      params: {
        requestId,
        code,
      },
    })
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.cancel
const cancelChangePhoneRequest = axios => requestId =>
  axios
    .put(
      `/authservice/api/v1/replace-user-primary-contact/cancel?requestId=${requestId}`,
    )
    .then(({ data }) => data.data)
    .catch(errorHandler);

// ReplaceUserPhoneResource.requests
const getChangePhoneRequests = axios => ({
  page,
  size,
  sort,
  status,
  search,
}) =>
  axios
    .post(
      "/authservice/api/v1/replace-user-primary-contact/requests",
      {
        status,
        searchFilter: search || undefined,
      },
      {
        params: { page, size, sort },
      },
    )
    .then(({ data }) => ({
      data: data.data.content,
      total: data.data.totalElements,
    }))
    .catch(errorHandler);

// UserResource.setUserFrozen
const freezeAccount = axios => accountId =>
  axios
    .put("/authservice/api/v1/user/set-user-frozen", undefined, {
      params: { accountId },
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then(({ data }) => data.data)
    .catch(errorHandler);

// UserResource.unsetUserFrozen
const unfreezeAccount = axios => accountId =>
  axios
    .put("/authservice/api/v1/user/unset-user-frozen", undefined, {
      params: { accountId },
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then(({ data }) => data.data)
    .catch(errorHandler);

const getUsers = axios => (page, size, filter = "", sort = "id,desc") =>
  axios
    .get(
      `/api/users?${querystring.stringify({
        page,
        size,
        filter,
        sort,
      })}`,
    )
    .then(({ data }) => ({
      data: data.content,
      total: data.totalElements,
    }))
    .catch(errorHandler);

const createUser = axios => payload =>
  axios
    .post("/api/users", payload)
    .then(({ data }) => data.data)
    .catch(errorHandler);

const updateUser = axios => payload =>
  axios
    .post("/api/users/edit", payload)
    .then(({ data }) => data.data)
    .catch(errorHandler);

const blockUser = axios => id =>
  axios
    .post(`/api/users/block/${id}`)
    .then(({ data }) => data.data)
    .catch(errorHandler);

const unblockUser = axios => id =>
  axios
    .post(`/api/users/unblock/${id}`)
    .then(({ data }) => data.data)
    .catch(errorHandler);

const deleteUser = axios => accountId =>
  axios
    .post("/tps/api/v2/fst-iroha-accounts/delete-account", {
      accountId,
    })
    .then(({ data }) => data)
    .catch(errorHandler);

export default {
  getAllMobileUser: getAllMobileUser(axiosInstance),
  getAllDesktopUser: getAllDesktopUser(axiosInstance),
  getAllWebUser: getAllWebUser(axiosInstance),
  getUserInfoByAccountId: getUserInfoByAccountId(axiosInstance),
  getUserInfoByAccountName: getUserInfoByAccountName(axiosInstance),
  getUserInfoByPhoneNumber: getUserInfoByPhoneNumber(axiosInstance),
  getUserAuthorizersList: getUserAuthorizersList(axiosInstance),
  getUserPotentialAuthorizers: getUserPotentialAuthorizers(axiosInstance),
  getUserAuthorizersHistory: getUserAuthorizersHistory(axiosInstance),
  addAuthorizer: addAuthorizer(axiosInstance),
  removeAuthorizer: removeAuthorizer(axiosInstance),
  transferMoney: transferMoney(axiosInstance),
  sendKeyRecoveryRequest: sendKeyRecoveryRequest(axiosInstance),

  createChangePhoneRequest: createChangePhoneRequest(axiosInstance),
  getChangePhoneRequests: getChangePhoneRequests(axiosInstance),
  resendSms: resendSms(axiosInstance),
  authorizeChangePhoneRequest: authorizeChangePhoneRequest(axiosInstance),
  verifyChangePhoneRequest: verifyChangePhoneRequest(axiosInstance),
  rejectChangePhoneRequest: rejectChangePhoneRequest(axiosInstance),
  cancelChangePhoneRequest: cancelChangePhoneRequest(axiosInstance),

  freezeAccount: freezeAccount(axiosInstance),
  unfreezeAccount: unfreezeAccount(axiosInstance),

  getUsers: getUsers(axiosInstance),
  createUser: createUser(axiosInstance),
  updateUser: updateUser(axiosInstance),
  blockUser: blockUser(axiosInstance),
  unblockUser: unblockUser(axiosInstance),
  deleteUser: deleteUser(axiosInstance),
};
